import React, { useEffect, useRef } from 'react';
import './WhatOculoChanges.css';

const BRAND_GRAD = 'linear-gradient(90deg,#6366F1,#8B5CF6 55%,#D946EF)';
const CONIC_A = 'conic-gradient(#6366F1 0turn,#8B5CF6 0.45turn,#D946EF 0.45turn 0.72turn,rgba(255,255,255,0.10) 0.72turn 1turn)';
const CONIC_B = 'conic-gradient(#6366F1 0turn,#8B5CF6 0.6turn,#D946EF 0.6turn 0.82turn,rgba(255,255,255,0.10) 0.82turn 1turn)';

const VALUE_CARDS = [
  { icon: 'bx-transfer-alt', num: '01', title: 'Go digital, change nothing',    desc: 'Your reports stay exactly as they are. Oculo digitises them in place: no new forms, no retraining, no change to how your teams work.' },
  { icon: 'bx-shuffle',      num: '02', title: 'Works Across Every Contractor', desc: 'Oculo reads the reports from every service company you work with and normalises them into a single, consistent record.' },
  { icon: 'bx-trending-up',  num: '03', title: 'Analytics that drive savings',  desc: 'Once your records are structured, Oculo surfaces trends across projects, contractors, and asset types, driving quality improvements and cost savings.' },
];

function Spark({ pts, h, w, id }) {
  const max = Math.max(...pts), min = Math.min(...pts);
  const span = max - min || 1, step = w / (pts.length - 1);
  const xy = pts.map((p, i) => [i * step, h - ((p - min) / span) * h]);
  const line = xy.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const area = line + ' L' + w + ' ' + h + ' L0 ' + h + ' Z';
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={`ws${id}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#6366F1"/><stop offset="0.5" stopColor="#8B5CF6"/><stop offset="1" stopColor="#D946EF"/>
        </linearGradient>
        <linearGradient id={`wf${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgba(139,92,246,0.35)"/><stop offset="1" stopColor="rgba(139,92,246,0)"/>
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#wf${id})`} />
      <path d={line} fill="none" stroke={`url(#ws${id})`} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AreaCard({ pts, h, w, id }) {
  return (
    <div className="woc-panel">
      <div className="woc-phead"><span className="woc-t" /><span className="woc-p" /></div>
      <Spark pts={pts} h={h} w={w} id={id} />
    </div>
  );
}

function BarCard({ heights, panelHeight }) {
  return (
    <div className="woc-panel">
      <div className="woc-bars" style={{ height: panelHeight }}>
        {heights.map((h, i) => (
          <i key={i} className={'woc-bar' + (i % 3 === 2 ? ' woc-hot' : '')} style={{ height: h }} />
        ))}
      </div>
    </div>
  );
}

function DonutCard({ size, stops, rows }) {
  const hole = Math.round(size * 0.6);
  return (
    <div className="woc-panel">
      <div className="woc-donutrow">
        <div className="woc-donut" style={{ width: size, height: size, background: stops }}>
          <div className="woc-hole" style={{ width: hole, height: hole }} />
        </div>
        <div className="woc-kpis">
          {rows.map((f, i) => (
            <div key={i} className="woc-kpi-row"><span style={{ width: `${f * 100}%` }} /></div>
          ))}
        </div>
      </div>
    </div>
  );
}

const BG_CARDS = [
  { pos: { right: '2%',  top: '4%',    width: 372 }, opacity: 0.18, blur: true,  vspeed: -72, chart: <AreaCard  pts={[12,22,16,30,26,42,36,54,48,68]} h={92}  w={340} id="1" /> },
  { pos: { left: '2%',   top: '40%',   width: 320 }, opacity: 0.15, blur: false, vspeed:  94, chart: <BarCard   heights={[30,52,40,70,56,86,64,46,74]} panelHeight={150} /> },
  { pos: { left: '-5%',  top: '2%',    width: 316 }, opacity: 0.13, blur: false, vspeed:  64, chart: <BarCard   heights={[34,54,42,70,58,84,66,48,72]} panelHeight={142} /> },
  { pos: { left: '37%',  top: '24%',   width: 312 }, opacity: 0.12, blur: true,  vspeed: -58, chart: <AreaCard  pts={[18,30,24,40,34,52,44,66,58,80]} h={80}  w={280} id="2" /> },
  { pos: { right: '-6%', top: '36%',   width: 300 }, opacity: 0.13, blur: false, vspeed:  72, chart: <BarCard   heights={[40,58,48,30,64,52,78,60]}    panelHeight={134} /> },
  { pos: { left: '42%',  bottom: '-7%',width: 326 }, opacity: 0.12, blur: true,  vspeed: -90, chart: <AreaCard  pts={[26,16,34,28,46,38,56,48,64,54]} h={76}  w={294} id="3" /> },
  { pos: { left: '9%',   bottom: '1%', width: 214 }, opacity: 0.12, blur: false, vspeed:  84, chart: <DonutCard size={74} stops={CONIC_A} rows={[0.7, 0.44, 0.6]}  /> },
  { pos: { right: '13%', bottom: '6%', width: 220 }, opacity: 0.15, blur: false, vspeed: -48, chart: <DonutCard size={80} stops={CONIC_B} rows={[0.82, 0.5, 0.66]} /> },
];

function WhatOculoChanges() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    let ticking = false;

    function update() {
      ticking = false;
      const H = window.innerHeight;
      const r = section.getBoundingClientRect();
      const prog = Math.max(0, Math.min(1, (H - r.top) / (H + r.height)));
      section.querySelectorAll('[data-vspeed]').forEach(el => {
        const sp = parseFloat(el.getAttribute('data-vspeed')) || 0;
        el.style.transform = `translateY(${((prog - 0.5) * sp).toFixed(1)}px)`;
      });
    }

    function onScroll() {
      if (reduce || ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <section ref={sectionRef} style={{ position: 'relative', background: '#15102A', padding: '124px 56px', overflow: 'hidden' }}>

      {/* Drifting analytics background */}
      <div className="woc-bg">
        <div className="woc-glow woc-glow-a" />
        <div className="woc-glow woc-glow-b" />
        {BG_CARDS.map((c, i) => (
          <div
            key={i}
            className="woc-card"
            data-vspeed={c.vspeed}
            style={{ ...c.pos, opacity: c.opacity, filter: c.blur ? 'blur(0.4px)' : undefined }}
          >
            {c.chart}
          </div>
        ))}
      </div>

      {/* Foreground content */}
      <div style={{ position: 'relative', zIndex: 2, maxWidth: 1180, margin: '0 auto' }}>
        <span className="oc-reveal" style={{ font: '700 12px Manrope,sans-serif', letterSpacing: '0.16em', color: '#816BFF' }}>WHAT OCULO CHANGES</span>
        <h2 className="oc-reveal" style={{ margin: '18px 0 0', maxWidth: 900, fontFamily: 'Montserrat,sans-serif', fontWeight: 800, fontSize: 48, lineHeight: 1.1, letterSpacing: '-0.02em', color: '#fff' }}>
          Paper records in.<br />Quality decisions out.
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24, marginTop: 64 }} className="oc-grid-3">
          {VALUE_CARDS.map((card) => (
            <div key={card.num} className="oc-reveal" style={{ position: 'relative', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '32px 28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
                <div style={{ width: 54, height: 54, borderRadius: 13, background: 'rgba(99,102,241,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className={'bx ' + card.icon} style={{ fontSize: 28, background: BRAND_GRAD, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }} />
                </div>
                <span style={{ font: '800 13px Manrope,sans-serif', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.22)' }}>{card.num}</span>
              </div>
              <h3 style={{ margin: 0, fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 21, lineHeight: 1.25, color: '#fff' }}>{card.title}</h3>
              <p style={{ margin: '12px 0 0', fontFamily: 'Montserrat,sans-serif', fontSize: 15.5, lineHeight: 1.62, color: 'rgba(255,255,255,0.62)' }}>{card.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default WhatOculoChanges;
